import React from 'react';
import {
  Alert, Button, Grid, Paper, Stack, TextField, Typography,
} from '@mui/material';
import AutoCodeCombo from '../combos/AutoCodeCombo';
import { GridColDef } from '@mui/x-data-grid';
import { FDataGrid, FGridMoneyCell } from '../grid';
import { HistoricalEntryDraft } from './bieHistorical.models';

type Props = {
  rows: HistoricalEntryDraft[];
  loading?: boolean;
  metricCode: string;
  fiscalYear: string;
  onMetricChange: (code: string, option?: any) => void;
  onFiscalYearChange: (year: string) => void;
  onGenerate: () => Promise<void>;
  onRowsChange: (rows: HistoricalEntryDraft[]) => void;
  onSave: () => Promise<void>;
};

class HistoricalManualEntryGrid extends React.Component<Props> {
  columns: GridColDef[] = [
    { field: 'metriccode', headerName: 'Variable', width: 170 },
    { field: 'productcode', headerName: 'Product', width: 135 },
    { field: 'productname', headerName: 'Product Name', flex: 1, minWidth: 210 },
    { field: 'periodcode', headerName: 'Period', width: 120 },
    { field: 'quantity', headerName: 'Quantity', width: 130, type: 'number', editable: true },
    { field: 'unitprice', headerName: 'Unit Price', width: 130, editable: true, renderCell: FGridMoneyCell },
    { field: 'amount', headerName: 'Amount', width: 160, editable: true, renderCell: FGridMoneyCell },
    { field: 'remarks', headerName: 'Remarks', flex: 1, minWidth: 200, editable: true },
  ];

  private processRowUpdate = (newRow:any, oldRow:any) => {
    const amount =
      Number(newRow.amount || 0) ||
      Number(newRow.quantity || 0) * Number(newRow.unitprice || 0);

    const updated = {
      ...newRow,
      amount,
      __dirty: true,
    };

    this.props.onRowsChange(
      this.props.rows.map((r) =>
        String(r.rid) === String(oldRow.rid) ? updated : r,
      ),
    );

    return updated;
  };

  render() {
    return (
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack spacing={2}>
            <div>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Manual Historical Entry
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a historical variable and fiscal year, then generate the expected product-period matrix.
              </Typography>
            </div>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <AutoCodeCombo
                  service="biehistoricalmetric"
                  label="Historical Variable"
                  value={this.props.metricCode || null}
                  onChange={(value, option) =>
                    this.props.onMetricChange(String(value || ''), option)
                  }
                  params={{ hst: 1 }}
                  minimumSearchLength={0}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  size="small"
                  label="Fiscal Year"
                  value={this.props.fiscalYear}
                  onChange={(e) => this.props.onFiscalYearChange(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => void this.props.onGenerate()}
                  disabled={
                    this.props.loading ||
                    !this.props.metricCode ||
                    !this.props.fiscalYear
                  }
                  fullWidth
                >
                  Generate Expected Matrix
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        {!this.props.rows.length && (
          <Alert severity="info">
            Generate the matrix to prepare the expected product and period rows.
          </Alert>
        )}

        <FDataGrid
          title="Historical Fact Entry"
          subtitle="Enter volumes, unit prices, values, and remarks. Product and period are generated from configuration."
          rows={this.props.rows}
          columns={this.columns}
          loading={this.props.loading}
          height={520}
          getRowId={(r:any) => r.rid}
          processRowUpdate={this.processRowUpdate}
          onProcessRowUpdateError={(error:any) => console.error(error)}
          showQuickFilter
          saveAll={this.props.onSave}
          pageSizeOptions={[25,50,100]}
        />
      </Stack>
    );
  }
}

export default HistoricalManualEntryGrid;
